import React, { useMemo, useRef } from 'react';
import { ValuesCache } from '@leverege/value-cache';
import { Config } from '@leverege/plugin';
import { v4 } from '@leverege/base62-util';
import DataViewerWrapper from "./DataViewerWrapper.js";
/**
 * Installs a a function to update the settings object for a DataViewer. It will run settings updaters
 * from four sources: a null settings updater, a set of updaters defined in Plugin's Config object at the paths
 * 'DataViewerSettingsUpdater/{dataViewer.type}/preUpdaters', * any updaters defined on the dataViewer's 
 * `settingsUpdaters` field, and finally, any updaters defined in Config's'DataViewerSettingsUpdater/{dataViewer.type}/postUpdaters'.
 * Each functino will be given an object containing { settings, props, caches, dataViewer } as the arguement. It should return
 * either settings, or a new settings (immutable set via model-utils or other non-destructive mechanism). If an updater
 * return null, or throws an exception, its contributions will be ignored
 * @param {Object} props the DataViewer's props (as well a prop for the dataViewer)
 */
export default function DataViewerRenderer(props) {
  const {
    dataViewer,
    settings,
    forwardRef
  } = props;
  const dataViewerInstanceId = useRef(v4());
  const caches = useMemo(() => {
    return new ValuesCache();
  }, []);
  const nSettings = useMemo(() => {
    let r = settings;
    r = nullSettingsUpdater({
      settings: r,
      props,
      caches,
      dataViewer
    }) || r;
    r = preConfigUpdaters({
      settings: r,
      props,
      caches,
      dataViewer
    }) || r;
    r = dataViewerUpdaters({
      settings: r,
      props,
      caches,
      dataViewer
    }) || r;
    r = postConfigUpdaters({
      settings: r,
      props,
      caches,
      dataViewer
    }) || r;
    return r;
  }, [settings]);
  const component = dataViewer.component || dataViewer.render;
  if (nSettings === settings) {
    return /*#__PURE__*/React.createElement(DataViewerWrapper, {
      ...props,
      forwardRef,
      dataViewerInstanceId,
      Component: component
    });
  }
  return /*#__PURE__*/React.createElement(DataViewerWrapper, {
    ...props,
    settings: nSettings,
    forwardRef,
    dataViewerInstanceId,
    Component: component
  });
}
function runUpdaters(updaters, opts) {
  let r = opts.settings;
  if (updaters == null) {
    return r;
  }
  if (typeof updaters === 'function') {
    return updaters(opts) || r;
  }
  if (Array.isArray(updaters)) {
    for (let n = 0; n < updaters.length; n++) {
      if (typeof updaters[n] === 'function') {
        try {
          r = updaters[n]({
            ...opts,
            settings: r
          }) || r;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }
    }
  }
  return r;
}

/**
 * Runs the function(s) at the specified path
 * @param {object} opts the updater options object
 * @param {string} type the path into config to lookup
 * @returns 
 */
function runConfigUpdaters(opts, type) {
  const {
    props
  } = opts;
  const {
    dataViewerModel
  } = props;
  if (dataViewerModel?.type) {
    const updaters = Config.get('DataViewerSettingsUpdater', `${dataViewerModel.type}/${type}`);
    return runUpdaters(updaters, opts);
  }
  return opts.settings;
}
function preConfigUpdaters(opts) {
  return runConfigUpdaters(opts, 'preUpdaters');
}
function postConfigUpdaters(opts) {
  return runConfigUpdaters(opts, 'postUpdaters');
}
function dataViewerUpdaters(opts) {
  const {
    dataViewer
  } = opts;
  return runUpdaters(dataViewer?.settingsUpdaters, opts);
}
function nullSettingsUpdater({
  settings,
  props,
  dataViewer
}) {
  if (settings == null) {
    if (dataViewer && typeof dataViewer.createSettings === 'function') {
      return dataViewer.createSettings(props);
    }
  }
  return settings;
}
export { nullSettingsUpdater, preConfigUpdaters, postConfigUpdaters };